import { useCallback, useEffect, useRef } from "react"
import "spoilerjs/spoiler-span"
import { openExternalLink } from "@/shared/lib/open-external-link"

interface TelegramMarkdownProps {
	children: string
	className?: string
}

function escapeHtml(text: string): string {
	return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function escapeHtmlAttr(text: string): string {
	return escapeHtml(text).replace(/"/g, "&quot;")
}

function applyInlineMarkup(text: string): string {
	return text
		.replace(/\*\*([\s\S]+?)\*\*/g, "<strong>$1</strong>")
		.replace(/__([\s\S]+?)__/g, "<em>$1</em>")
		.replace(/\+\+([\s\S]+?)\+\+/g, "<u>$1</u>")
		.replace(/~~([\s\S]+?)~~/g, "<del>$1</del>")
		.replace(/\|\|([\s\S]+?)\|\|/g, "<spoiler-span>$1</spoiler-span>")
}

function sanitizeUrl(url: string): string | null {
	const trimmed = url.trim()
	if (!trimmed || /[\s"'<>]/.test(trimmed)) {
		return null
	}

	if (/^tg:/i.test(trimmed)) {
		return trimmed
	}

	try {
		const normalized = trimmed.startsWith("www.") ? `https://${trimmed}` : trimmed
		const parsed = new URL(normalized)
		if (parsed.protocol === "http:" || parsed.protocol === "https:") {
			return parsed.href
		}
	} catch {
		return null
	}

	return null
}

export function TelegramMarkdown({ children, className = "" }: TelegramMarkdownProps) {
	const handleLinkClick = useCallback((e: MouseEvent) => {
		const target = e.target as HTMLElement
		if (target.tagName === "A") {
			e.preventDefault()
			const href = target.getAttribute("href")
			if (href) openExternalLink(href)
		}
	}, [])

	const containerRef = useRef<HTMLSpanElement>(null)

	useEffect(() => {
		const container = containerRef.current
		if (!container) return
		container.addEventListener("click", handleLinkClick as EventListener)
		return () => {
			container.removeEventListener("click", handleLinkClick as EventListener)
		}
	}, [handleLinkClick])

	const convertToHtml = (text: string): string => {
		let result = text

		const codeBlocks: string[] = []
		result = result.replace(/```([\s\S]*?)```/g, (_, code) => {
			const trimmedCode = code.replace(/^\n+|\n+$/g, "")
			codeBlocks.push(`<pre><code>${escapeHtml(trimmedCode)}</code></pre>`)
			return `\x00CODE_BLOCK_${codeBlocks.length - 1}\x00`
		})

		const inlineCodes: string[] = []
		result = result.replace(/`(.+?)`/g, (_, code) => {
			inlineCodes.push(`<code>${escapeHtml(code)}</code>`)
			return `\x00INLINE_CODE_${inlineCodes.length - 1}\x00`
		})

		const links: string[] = []
		result = result.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, linkText, url) => {
			const safeUrl = sanitizeUrl(url)
			const safeText = applyInlineMarkup(escapeHtml(linkText))
			if (!safeUrl) {
				links.push(safeText)
				return `\x00LINK_${links.length - 1}\x00`
			}
			links.push(`<a href="${escapeHtmlAttr(safeUrl)}">${safeText}</a>`)
			return `\x00LINK_${links.length - 1}\x00`
		})

		const plainUrls: string[] = []
		result = result.replace(/(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/g, (match) => {
			const safeUrl = sanitizeUrl(match)
			if (!safeUrl) {
				plainUrls.push(escapeHtml(match))
				return `\x00PLAIN_URL_${plainUrls.length - 1}\x00`
			}
			plainUrls.push(`<a href="${escapeHtmlAttr(safeUrl)}">${escapeHtml(match)}</a>`)
			return `\x00PLAIN_URL_${plainUrls.length - 1}\x00`
		})

		const blockquotes: string[] = []
		result = result.replace(/^(>+)(.*)$/gm, (_, arrows, content) => {
			const level = arrows.length
			const safeContent = applyInlineMarkup(escapeHtml(content.trim()))
			blockquotes.push(`<blockquote data-level="${level}">${safeContent}</blockquote>`)
			return `\x00BLOCKQUOTE_${blockquotes.length - 1}\x00`
		})

		result = result.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")

		result = applyInlineMarkup(result)

		links.forEach((link, index) => {
			result = result.replace(`\x00LINK_${index}\x00`, link)
		})

		plainUrls.forEach((url, index) => {
			result = result.replace(`\x00PLAIN_URL_${index}\x00`, url)
		})

		inlineCodes.forEach((code, index) => {
			result = result.replace(`\x00INLINE_CODE_${index}\x00`, code)
		})

		codeBlocks.forEach((block, index) => {
			result = result.replace(`\x00CODE_BLOCK_${index}\x00`, block)
		})

		blockquotes.forEach((quote, index) => {
			result = result.replace(`\x00BLOCKQUOTE_${index}\x00`, quote)
		})

		return result
	}

	return (
		<span
			ref={containerRef}
			className={`inline [&_strong]:font-bold [&_em]:italic [&_del]:line-through [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono [&_pre]:block [&_pre]:my-2 [&_pre]:p-3 [&_pre]:bg-muted [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_a]:text-primary [&_a]:underline [&_a:hover]:no-underline [&_a]:cursor-pointer [&_blockquote]:border-l-4 [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:my-1 [&_blockquote]:text-muted-foreground ${className}`}
			dangerouslySetInnerHTML={{ __html: convertToHtml(children) }}
		/>
	)
}
